import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowClockwise,
  BellRinging,
  CheckCircle,
  DeviceMobile,
  ShieldWarning,
  Warning,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "../../app/auth";
import { DataFreshness, MetricStrip, Modal, SearchInput, StatusTag } from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { useToast } from "../../components/Toast";
import { formatDateTime } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import type { Alert, AlertSummary } from "../../lib/types";

interface AlertRule {
  id: string;
  name: string;
  alertType: string;
  severity: string;
  enabled: boolean;
  config: Record<string, unknown>;
}

function AlertRulesModal({
  rules,
  onClose,
}: {
  rules: AlertRule[];
  onClose(): void;
}) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [name, setName] = useState("");
  const [thresholdDays, setThresholdDays] = useState("7");
  const create = useMutation({
    mutationFn: () => request("/api/alert-rules", {
      method: "POST",
      body: JSON.stringify({
        name,
        alertType: "device_token_expiring",
        severity: "warning",
        config: { thresholdDays: Number(thresholdDays) },
      }),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
      setName("");
      notify("告警规则已创建");
    },
  });
  const toggle = useMutation({
    mutationFn: (rule: AlertRule) => request(`/api/alert-rules/${rule.id}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled: !rule.enabled }),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
      notify("告警规则状态已更新");
    },
  });
  return (
    <Modal title="告警规则" description="规则和告警事件相互独立，修改规则不会覆盖历史事件。" onClose={onClose} footer={<button className="primary-button" onClick={onClose}>完成</button>}>
      <div className="form-stack">
        <div className="version-list">
          {rules.map((rule) => <div key={rule.id}><span><strong>{rule.name}</strong><small>{rule.alertType} · 阈值 {String(rule.config.thresholdDays ?? "—")} 天</small></span><button className="text-button" disabled={toggle.isPending} onClick={() => toggle.mutate(rule)}>{rule.enabled ? "停用" : "启用"}</button></div>)}
          {!rules.length && <p className="inline-empty">尚未创建告警规则。</p>}
        </div>
        <label><span>规则名称</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：设备凭证临期提醒" /></label>
        <label><span>提前提醒天数</span><input value={thresholdDays} onChange={(event) => setThresholdDays(event.target.value)} /></label>
        <button className="secondary-button compact" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>创建规则</button>
        {(create.isError || toggle.isError) && <p className="form-error">规则保存失败，请检查名称是否重复。</p>}
      </div>
    </Modal>
  );
}

function AlertActionModal({
  alertCount,
  action,
  onClose,
}: {
  alertCount: number;
  action: "rotate" | "notify";
  onClose(): void;
}) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      request(
        action === "rotate"
          ? "/api/tasks/device-token-rotation"
          : "/api/tasks/device-token-expiring-notifications",
        {
          method: "POST",
          body: JSON.stringify({ reason, limit: Math.min(Math.max(alertCount, 1), 100) }),
        },
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["alerts"] }),
      ]);
      notify(
        action === "rotate" ? "Token 轮换任务已创建" : "到期通知任务已创建",
        `覆盖 ${alertCount} 条活动告警`,
      );
      onClose();
    },
  });
  return (
    <Modal
      title={action === "rotate" ? "批量轮换设备 Token" : "发送到期通知"}
      description={`当前共有 ${alertCount} 台设备需要处理。`}
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button compact" onClick={onClose}>取消</button>
          <button className="primary-button" disabled={!reason.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? "正在创建…" : "创建处置任务"}
          </button>
        </>
      }
    >
      <div className="form-stack">
        <p className="operation-note">
          {action === "rotate"
            ? "系统会筛选需要轮换的设备并创建批量任务。设备离线时，任务会等待其重新连接。"
            : "系统会向需要处理的设备下发 Token 到期通知，不会立即更换凭证。"}
        </p>
        <label><span>处置原因</span><textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="必填，将写入任务参数和审计日志" /></label>
        {mutation.isError && <p className="form-error">处置任务创建失败，可能没有设备需要处理。</p>}
      </div>
    </Modal>
  );
}

export function AlertsPage() {
  const polling = Number(import.meta.env.VITE_QUERY_POLL_INTERVAL_MS || 10000);
  const { request, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const alertsQuery = useApiQuery<Alert[]>(["alerts"], "/api/alerts?limit=200", {
    refetchInterval: polling,
  });
  const summaryQuery = useApiQuery<AlertSummary>(["alerts", "summary"], "/api/alerts/summary", {
    refetchInterval: polling,
  });
  const rulesQuery = useApiQuery<AlertRule[]>(["alert-rules"], "/api/alert-rules");
  const [query, setQuery] = useState("");
  const [severity, setSeverity] = useState("all");
  const [state, setState] = useState("open");
  const [action, setAction] = useState<"rotate" | "notify" | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const transitionMutation = useMutation({
    mutationFn: (input: { id: string; transition: "acknowledge" | "resolve" }) =>
      request(`/api/alerts/${input.id}/${input.transition}`, {
        method: "POST",
        body: JSON.stringify({
          comment:
            input.transition === "acknowledge"
              ? "已在管理端确认并开始跟进"
              : "已在管理端完成处置",
        }),
      }),
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({ queryKey: ["alerts"] });
      notify(input.transition === "acknowledge" ? "告警已确认" : "告警已解决");
    },
  });
  const alerts = alertsQuery.data || [];
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return alerts.filter((alert) => {
      const matchesSeverity = severity === "all" || alert.severity === severity;
      const matchesState =
        state === "all" ||
        (state === "open"
          ? ["active", "acknowledged", "silenced"].includes(alert.status)
          : alert.status === state);
      const matchesQuery =
        !normalized ||
        [alert.title, alert.message, alert.resourceId, alert.device?.info?.model, alert.device?.account?.username]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);
      return matchesSeverity && matchesState && matchesQuery;
    });
  }, [alerts, query, severity, state]);
  const critical = alerts.filter((alert) => alert.severity === "critical").length;
  const warning = alerts.filter((alert) => alert.severity === "warning").length;
  const expired = alerts.filter((alert) => alert.device?.tokenExpired).length;

  if (alertsQuery.isError || summaryQuery.isError || rulesQuery.isError) {
    return <QueryErrorState onRetry={() => { void alertsQuery.refetch(); void summaryQuery.refetch(); void rulesQuery.refetch(); }} />;
  }

  return (
    <div className="module-page">
      <MetricStrip metrics={[
        { label: "活动告警", value: summaryQuery.data?.activeCount || 0, note: "当前需要处理的事件", tone: alerts.length ? "metric-red" : "metric-green" },
        { label: "严重", value: critical, note: "凭证已过期", tone: critical ? "metric-red" : undefined },
        { label: "警告", value: warning, note: "凭证即将过期" },
        { label: "影响设备", value: new Set(alerts.map((alert) => alert.resourceId)).size, note: `${expired} 台已过期` },
      ]} />
      <section className={`alert-actions ${!alerts.length ? "healthy" : ""}`}>
        <div>{alerts.length ? <ShieldWarning size={27} /> : <CheckCircle size={27} weight="fill" />}<span><strong>{alerts.length ? "设备凭证需要集中处理" : "告警监控运行正常"}</strong><small>{alerts.length ? "建议优先轮换已过期 Token，再处理即将过期设备。" : "告警规则持续生效，新事件会自动进入处置队列。"}</small></span></div>
        <span>
          <DataFreshness updatedAt={summaryQuery.data?.generatedAt} isFetching={alertsQuery.isFetching || summaryQuery.isFetching} onRefresh={() => { void alertsQuery.refetch(); void summaryQuery.refetch(); void rulesQuery.refetch(); }} />
          {hasPermission("alert-rules:manage") && <button className="secondary-button compact" onClick={() => setRulesOpen(true)}><ShieldWarning size={16} />规则 {rulesQuery.data?.length || 0}</button>}
          {alerts.length > 0 && hasPermission("tasks:create") && <><button className="secondary-button compact" onClick={() => setAction("notify")}><BellRinging size={16} />发送通知</button><button className="primary-button" onClick={() => setAction("rotate")}><ArrowClockwise size={16} />批量轮换</button></>}
        </span>
      </section>
      {!alertsQuery.isPending && !alerts.length ? (
        <section className="alert-clear-state">
          <CheckCircle size={44} weight="fill" />
          <h2>当前没有活动告警</h2>
          <p>设备凭证和连接状态正常，系统会持续监控并自动刷新。</p>
        </section>
      ) : (
        <>
          <section className="table-panel">
            <header className="module-toolbar">
              <div className="toolbar-left">
                <SearchInput value={query} onChange={setQuery} placeholder="搜索告警、设备或账号" />
                <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
                  <option value="all">全部级别</option>
                  <option value="critical">严重</option>
                  <option value="warning">警告</option>
                </select>
                <select value={state} onChange={(event) => setState(event.target.value)}>
                  <option value="open">待处理</option>
                  <option value="all">全部状态</option>
                  <option value="active">活动</option>
                  <option value="acknowledged">已确认</option>
                  <option value="resolved">已解决</option>
                  <option value="silenced">已静默</option>
                </select>
              </div>
              <span className="auto-refresh"><ArrowClockwise size={14} />每 {Math.round(polling / 1000)} 秒刷新</span>
            </header>
            <div className="alert-list">
              {filtered.map((alert) => (
                <article key={alert.id}>
                  <div className={`alert-icon ${alert.severity}`}><Warning size={20} weight="fill" /></div>
                  <div className="alert-copy">
                    <header><strong>{alert.title}</strong><StatusTag value={alert.severity} /><StatusTag value={alert.status} /></header>
                    <p>{alert.message}</p>
                    <span><DeviceMobile size={14} />{alert.device?.info?.model || `设备 #${alert.resourceId}`} · {alert.device?.account?.username || "未分配账号"}</span>
                    <nav className="alert-links" aria-label="告警关联操作">
                      <Link to={`/devices?deviceId=${encodeURIComponent(alert.resourceId)}`}>查看设备</Link>
                      {hasPermission("tasks:create") && <Link to={`/tasks?deviceId=${encodeURIComponent(alert.resourceId)}&fromAlert=${encodeURIComponent(alert.id)}`}>创建处置任务</Link>}
                      {hasPermission("audit:read") && <Link to={`/audit?resourceType=device&resourceId=${encodeURIComponent(alert.resourceId)}`}>查看审计</Link>}
                    </nav>
                  </div>
                  <div className="alert-meta">
                    <span>检测时间</span><strong>{formatDateTime(alert.detectedAt)}</strong><small>到期：{formatDateTime(alert.deviceTokenExpiresAt)}</small>
                    {hasPermission("alerts:manage") && alert.status !== "resolved" && (
                      <span className="row-actions">
                        {alert.status === "active" && (
                          <button disabled={transitionMutation.isPending} onClick={() => transitionMutation.mutate({ id: alert.id, transition: "acknowledge" })}>确认</button>
                        )}
                        <button disabled={transitionMutation.isPending} onClick={() => transitionMutation.mutate({ id: alert.id, transition: "resolve" })}>解决</button>
                      </span>
                    )}
                  </div>
                </article>
              ))}
              {!alertsQuery.isPending && !filtered.length && <div className="table-empty">没有符合当前筛选条件的告警。</div>}
              {alertsQuery.isPending && <div className="table-empty">正在读取告警…</div>}
            </div>
          </section>
        </>
      )}
      {action && <AlertActionModal alertCount={alerts.length} action={action} onClose={() => setAction(null)} />}
      {rulesOpen && <AlertRulesModal rules={rulesQuery.data || []} onClose={() => setRulesOpen(false)} />}
    </div>
  );
}
